"use strict";

var _Object$defineProperty = require("@babel/runtime-corejs3/core-js-stable/object/define-property");
var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
_Object$defineProperty(exports, "__esModule", {
  value: true
});
exports.InputWrapper = void 0;
var _filter = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/instance/filter"));
var _forEach = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/instance/for-each"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _MonthInput = require("./MonthInput");
var _DayInput = require("./DayInput");
var _YearInput = require("./YearInput");
var _validation = require("./lib/validation");
var _constants = require("./lib/constants");
/**
 * This is a wrapper that will render a month, day, or year input as determined by the inputType prop.
 * This is a stateful functional component, but state is actually maintained in the parent because
 * the parent needs to be aware of its children's state in order to facilitate auto-tabbing as
 * the user fills out the form.
 *
 * @param {Object} config The config object from which we pull labels, messages, etc.
 * @param {String} inputType One of [MONTH, DAY, YEAR], pulled from constants
 * @param {Array} parentErrors The error messages passed in from the parent, always renders if passed in
 * @param {Boolean} required Whether or not the date input is required, as determined by the consumer
 * @param {Object} state The current state of this input
 * @param {Function} setState The function that updates this component's state
 * @param {Object} ref The ref to the input DOM node
 */
const InputWrapper = exports.InputWrapper = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    config,
    inputType,
    parentErrors,
    required,
    state,
    setState
  } = _ref;
  /**
   * This is the onChange event handler for the input.
   * It will suppress non-integer values typed by end-users, run its simple internal
   * validation, then call the setState function passed in from the parent.
   * Updates the value, error, and lastEventType (facilitates auto-tabbing).
   *
   * @param {Object} event The onChange event fired by the input
   */
  const onChange = event => {
    const acceptedCharsRegEx = /[0-9]/;
    const inputChars = [...event.target.value];
    const filteredChars = (0, _filter.default)(inputChars).call(inputChars, char => {
      return char.match(acceptedCharsRegEx);
    });
    const value = filteredChars.join('');
    setState({
      ...state,
      value: value,
      error: getError(value),
      lastEventType: _constants.EVENT_TYPE_CHANGE
    });
  };

  /**
   * This is the onBlur event handler for the input.
   * Runs its simple internal validation then calls the setState passed in from the parent.
   * Updates touched, error, and lastEventType (facilitates auto-tabbing).
   *
   * @param {Object} event The onBlur event fired by the input
   */
  const onBlur = event => {
    setState({
      ...state,
      touched: true,
      isErrorVisible: true,
      error: getError(event.target.value),
      lastEventType: _constants.EVENT_TYPE_BLUR
    });
  };

  /**
   * The simple validation that this component runs.
   * Returns a single string as the error, or an empty string if validation passes.
   *
   * @param {String} value The value passed in from the onChange and onBlur event handlers
   * @returns {String} The error message
   */
  const getError = value => {
    if (required && (!value || value === '')) {
      return config.isRequiredMsg;
    } else if (!(0, _validation.validateInput)(inputType, value)) {
      return config.invalidMsg;
    } else {
      return '';
    }
  };

  /**
   * Returns an array of errors to render under the input.
   * If there are errors passed in from the parent, they will always get added.
   * Otherwise, return the error string from state (added by the getError function),
   * but only if the input has been touched (blurred), or already has a value, so we
   * do not render errors for incomplete input. Errors passed down and rendered here
   * will always be visually hidden, but read by screen readers (errors are rendered
   * visually in the parent)
   *
   * @returns {Array}
   */
  const getDisplayErrors = () => {
    let errors = [];
    if (state.isErrorVisible && state.error !== '') {
      errors.push(state.error);
    }
    if (parentErrors) {
      (0, _forEach.default)(parentErrors).call(parentErrors, error => {
        if (error && error !== '') {
          errors.push(error);
        }
      });
    }
    return errors;
  };
  switch (inputType) {
    case _constants.INPUT_TYPE_MONTH:
      return /*#__PURE__*/_react.default.createElement(_MonthInput.MonthInput, {
        config: config,
        errors: getDisplayErrors(),
        onBlur: onBlur,
        onChange: onChange,
        ref: ref,
        required: required,
        value: state.value
      });
    case _constants.INPUT_TYPE_DAY:
      return /*#__PURE__*/_react.default.createElement(_DayInput.DayInput, {
        config: config,
        errors: getDisplayErrors(),
        onBlur: onBlur,
        onChange: onChange,
        ref: ref,
        required: required,
        value: state.value
      });
    case _constants.INPUT_TYPE_YEAR:
    default:
      // will render the year input by default
      return /*#__PURE__*/_react.default.createElement(_YearInput.YearInput, {
        config: config,
        errors: getDisplayErrors(),
        onBlur: onBlur,
        onChange: onChange,
        ref: ref,
        required: required,
        value: state.value
      });
  }
});
InputWrapper.displayName = 'InputWrapper';
InputWrapper.propTypes = {
  /**
   * The config object that drives the construction of the input
   */
  config: _propTypes.default.shape({
    label: _propTypes.default.string.isRequired,
    name: _propTypes.default.string.isRequired,
    initialValue: _propTypes.default.string,
    placeholder: _propTypes.default.string,
    autoComplete: _propTypes.default.string,
    ariaDescription: _propTypes.default.string.isRequired,
    invalidMsg: _propTypes.default.string.isRequired,
    isRequiredMsg: _propTypes.default.string.isRequired
  }).isRequired,
  /**
   * The type of input, one of [MONTH, DAY, YEAR] (constants.js)
   */
  inputType: _propTypes.default.string.isRequired,
  /**
   * Any errors passed in from the consumer, which result from the
   * consumer's validation
   */
  parentErrors: _propTypes.default.arrayOf(_propTypes.default.string),
  /**
   * Whether or not the input is required
   */
  required: _propTypes.default.bool,
  /**
   * The current state of the input
   */
  state: _propTypes.default.shape({
    required: _propTypes.default.bool,
    value: _propTypes.default.string,
    touched: _propTypes.default.bool,
    error: _propTypes.default.string,
    lastEventType: _propTypes.default.string,
    isErrorVisible: _propTypes.default.bool.isRequired
  }).isRequired,
  /**
   * The function that updates the state
   */
  setState: _propTypes.default.func.isRequired
};
InputWrapper.__docgenInfo = {
  "description": "This is a wrapper that will render a month, day, or year input as determined by the inputType prop.\nThis is a stateful functional component, but state is actually maintained in the parent because\nthe parent needs to be aware of its children's state in order to facilitate auto-tabbing as\nthe user fills out the form.\n\n@param {Object} config The config object from which we pull labels, messages, etc.\n@param {String} inputType One of [MONTH, DAY, YEAR], pulled from constants\n@param {Array} parentErrors The error messages passed in from the parent, always renders if passed in\n@param {Boolean} required Whether or not the date input is required, as determined by the consumer\n@param {Object} state The current state of this input\n@param {Function} setState The function that updates this component's state\n@param {Object} ref The ref to the input DOM node",
  "methods": [],
  "displayName": "InputWrapper",
  "props": {
    "config": {
      "description": "The config object that drives the construction of the input",
      "type": {
        "name": "shape",
        "value": {
          "label": {
            "name": "string",
            "required": true
          },
          "name": {
            "name": "string",
            "required": true
          },
          "initialValue": {
            "name": "string",
            "required": false
          },
          "placeholder": {
            "name": "string",
            "required": false
          },
          "autoComplete": {
            "name": "string",
            "required": false
          },
          "ariaDescription": {
            "name": "string",
            "required": true
          },
          "invalidMsg": {
            "name": "string",
            "required": true
          },
          "isRequiredMsg": {
            "name": "string",
            "required": true
          }
        }
      },
      "required": true
    },
    "inputType": {
      "description": "The type of input, one of [MONTH, DAY, YEAR] (constants.js)",
      "type": {
        "name": "string"
      },
      "required": true
    },
    "parentErrors": {
      "description": "Any errors passed in from the consumer, which result from the\nconsumer's validation",
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "string"
        }
      },
      "required": false
    },
    "required": {
      "description": "Whether or not the input is required",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "state": {
      "description": "The current state of the input",
      "type": {
        "name": "shape",
        "value": {
          "required": {
            "name": "bool",
            "required": false
          },
          "value": {
            "name": "string",
            "required": false
          },
          "touched": {
            "name": "bool",
            "required": false
          },
          "error": {
            "name": "string",
            "required": false
          },
          "lastEventType": {
            "name": "string",
            "required": false
          },
          "isErrorVisible": {
            "name": "bool",
            "required": true
          }
        }
      },
      "required": true
    },
    "setState": {
      "description": "The function that updates the state",
      "type": {
        "name": "func"
      },
      "required": true
    }
  }
};